import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/3cbb7eeae4c2726e4016b796f003e1c2.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2 text-center lg:text-left"
            >
              <h1 className="font-serif text-6xl lg:text-7xl text-[#F8F6F3] leading-tight mb-6">
                Edward J.<br />Dumke
              </h1>
              <p className="font-serif italic text-2xl text-[#C9A961] mb-12">
                San Francisco Stories & Family Sagas
              </p>
              <Link
                to="/books"
                className="inline-block px-10 py-4 bg-[#8B2635] text-[#F8F6F3] rounded-lg text-lg font-medium hover:bg-[#6d1e29] transition-all shadow-lg whitespace-nowrap"
              >
                Explore Books
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:w-1/2 flex justify-center lg:justify-end mt-12 lg:mt-0"
            >
              <div className="relative">
                <img
                  src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/85c5105b512ab525c6805a40d3f56727.jpeg"
                  alt="Family Secrets: A San Francisco Saga"
                  className="w-80 h-auto rounded-lg shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-300"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Book Section */}
      <section className="py-24 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/85c5105b512ab525c6805a40d3f56727.jpeg"
                alt="Family Secrets"
                className="w-full max-w-md mx-auto rounded-lg shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-sm tracking-widest text-[#C9A961] uppercase">
                Featured Novel
              </span>
              <h2 className="font-serif text-5xl text-[#2C2C2C] mt-4 mb-6">
                Family Secrets:<br />A San Francisco Saga
              </h2>
              <p className="text-lg text-[#666666] leading-relaxed mb-8">
                A captivating journey through generations of a San Francisco family, 
                where hidden truths and long-buried secrets emerge to reshape the 
                present. Set against the backdrop of the City by the Bay's rich 
                history, this compelling saga explores the bonds that tie families 
                together and the revelations that can tear them apart.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://www.amazon.com/Family-Secrets-San-Francisco-Saga/dp/196736172X"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-[#8B2635] text-white rounded-full hover:bg-[#6d1e29] transition-all shadow-md whitespace-nowrap"
                >
                  View on Amazon
                </a>
                <Link
                  to="/books"
                  className="px-8 py-3 bg-white border-2 border-[#8B2635] text-[#8B2635] rounded-full hover:bg-[#8B2635] hover:text-white transition-all whitespace-nowrap"
                >
                  All Books
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-sm tracking-widest text-[#C9A961] uppercase">
              About the Author
            </span>
            <h2 className="font-serif text-5xl text-[#2C2C2C] mt-4 mb-8">
              Stories Rooted in San Francisco
            </h2>
            <p className="text-lg text-[#666666] leading-relaxed max-w-3xl mx-auto mb-12">
              Edward Dumke, a native of the San Francisco Bay Area, has always held a 
              deep fascination for the City by the Bay. For over sixty years he has been 
              a keen observer of the San Francisco scene with all its unparalleled beauty, 
              diversity, and rich cultural heritage. His novels bring to life the stories 
              of families navigating the complexities of love, loss, and legacy against 
              the iconic backdrop of San Francisco.
            </p>
            <Link
              to="/about"
              className="inline-block px-8 py-3 bg-[#8B2635] text-white rounded-full hover:bg-[#6d1e29] transition-all shadow-md whitespace-nowrap"
            >
              Learn More
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      <section className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl text-[#2C2C2C] mb-4">
              From the Author's Journal
            </h2>
            <p className="text-lg text-[#666666]">
              Insights on writing, San Francisco history, and the creative process
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'The Making of Family Secrets',
                category: 'Writing Process',
                date: 'March 15, 2024',
                excerpt:
                  'Discover the inspiration behind my latest novel and the journey of bringing these characters to life...',
                image: 'https://readdy.ai/api/search-image?query=vintage%20typewriter%20on%20wooden%20desk%20with%20coffee%20cup%20and%20manuscript%20pages%20in%20warm%20golden%20lighting%20creating%20a%20nostalgic%20writing%20atmosphere%20with%20soft%20shadows%20and%20literary%20ambiance&width=600&height=400&seq=blog-writing-process&orientation=landscape',
              },
              {
                title: 'San Francisco Through the Decades',
                category: 'History',
                date: 'March 8, 2024',
                excerpt:
                  'Exploring how the City by the Bay has transformed over generations while maintaining its unique character...',
                image: 'https://readdy.ai/api/search-image?query=historic%20black%20and%20white%20photograph%20of%20San%20Francisco%20cable%20cars%20and%20Victorian%20houses%20on%20steep%20hills%20with%20vintage%20street%20lamps%20creating%20a%20timeless%20nostalgic%20atmosphere&width=600&height=400&seq=blog-sf-history&orientation=landscape',
              },
              {
                title: 'Character Development in Family Sagas',
                category: 'Craft',
                date: 'February 28, 2024',
                excerpt:
                  'How to create multi-generational characters that resonate with readers across different time periods...',
                image: 'https://readdy.ai/api/search-image?query=open%20vintage%20family%20photo%20album%20with%20sepia%20photographs%20and%20handwritten%20notes%20on%20antique%20wooden%20table%20with%20soft%20natural%20lighting%20creating%20an%20intimate%20nostalgic%20mood&width=600&height=400&seq=blog-character-dev&orientation=landscape',
              },
            ].map((post, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="relative h-56 w-full overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-1 bg-[#8B2635] text-white text-xs rounded-full whitespace-nowrap">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-[#2C2C2C] mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <div className="flex items-center text-sm text-[#666666] mb-4">
                    <span>{post.date}</span>
                    <span className="mx-2 text-[#C9A961]">•</span>
                    <span>5 min read</span>
                  </div>
                  <p className="text-[#666666] leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link
                    to="/blog"
                    className="inline-flex items-center text-[#8B2635] hover:text-[#6d1e29] transition-colors whitespace-nowrap"
                  >
                    Read More
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/blog"
              className="inline-block px-8 py-3 border-2 border-[#8B2635] text-[#8B2635] rounded-lg hover:bg-[#8B2635] hover:text-white transition-all whitespace-nowrap"
            >
              View All Posts
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;